// Minimal observable store for app-level state. Components subscribe and re-render
// on change (Lit components call store.subscribe in connectedCallback).

export interface SessionUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  profile_photo_url: string | null;
  user_type: string;
  is_student: boolean;
  is_teacher: boolean;
  status: string;
  language_preference: string;
  dark_mode: boolean;
  role: { id: string; name: string };
  permissions: Record<string, boolean>;
}

export type FeatureFlags = Record<string, boolean>;

export interface SchoolBrand { name: string; logo_url: string | null; default_language?: string; theme_id?: string }

export interface AppState {
  ready: boolean;
  setupComplete: boolean; // first-run wizard finished (false → force /setup)
  user: SessionUser | null;
  features: FeatureFlags;
  breakPeriod: boolean; // current month is a configured break month
  school: SchoolBrand | null; // school name + logo for nav branding
  darkMode: boolean;
  themeId: string; // active school-wide colour theme
  locale: string;
}

type Listener = (state: AppState) => void;

class Store {
  private state: AppState = {
    ready: false,
    // Default true so a transient /setup/status failure never traps users in the wizard;
    // boot() sets the real value before the app renders.
    setupComplete: true,
    user: null,
    features: {},
    breakPeriod: false,
    school: null,
    darkMode: document.documentElement.dataset.theme === 'dark',
    themeId: document.documentElement.dataset.themeId || 'ocean',
    locale: 'en',
  };
  private listeners = new Set<Listener>();

  get(): AppState {
    return this.state;
  }

  set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get isAuthenticated(): boolean {
    return this.state.user !== null;
  }

  hasPermission(flag: string): boolean {
    return this.state.user?.permissions?.[flag] === true;
  }

  /** A feature flag is enabled unless explicitly set to false (default-on). */
  featureEnabled(flag: string): boolean {
    return this.state.features[flag] !== false;
  }

  /** Apply dark mode to the document and persist the choice for first-paint. */
  applyTheme(dark: boolean): void {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    try {
      localStorage.setItem('cadence.theme', dark ? 'dark' : 'light');
    } catch {
      /* ignore storage errors */
    }
    this.set({ darkMode: dark });
  }

  /** Apply the school-wide colour theme and cache it for first-paint. */
  applyThemeId(id: string): void {
    document.documentElement.dataset.themeId = id;
    try {
      localStorage.setItem('cadence.theme-id', id);
    } catch {
      /* ignore storage errors */
    }
    this.set({ themeId: id });
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.state);
  }
}

export const store = new Store();
